import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useUpdateDistributorCompanyMutation } from 'api/main';
import { userSelectors } from 'reducers';
import { jsonParseSafe } from 'utils/helpers';

import EditableEmailAddress from './EditableEmailAddress';
import CommunicationEmailAdd from './CommunicationEmailAdd';
import { ExpandableList } from 'components';
import { Box } from '@mui/material';

const MAX_ADDRESSES = 20;
const MIN_ADDRESSES = 1;

const DistributorEmailList = ({ emailsKey, title, distributor }) => {
    const mounted = useRef(false);

    const [updateDistributorCompany] = useUpdateDistributorCompanyMutation();

    const internalUser = useSelector(userSelectors.getInternalUser);

    const distributorCompany = useMemo(
        () => distributor || internalUser?.subscriptions[0]?.distributorCompany,
        [internalUser, distributor]
    );
    const addresses = useMemo(
        () => jsonParseSafe(distributorCompany[emailsKey], []),
        [distributorCompany, emailsKey]
    );

    const handleDeleteAddress = useCallback(
        index => callback => {
            const newAddressList = addresses.filter((_, i) => i !== index);
            updateDistributorCompany({
                payload: {
                    id: distributorCompany.id,
                    emails: newAddressList,
                    emailsKey,
                },
                callback,
            });
        },
        [updateDistributorCompany, addresses, distributorCompany, emailsKey]
    );

    const handleUpdateAddress = useCallback(
        index => (newValue, callback) => {
            const newAddressList = [...addresses];
            newAddressList[index] = newValue;
            updateDistributorCompany({
                payload: {
                    id: distributorCompany.id,
                    emails: newAddressList,
                    emailsKey,
                },
                callback,
            });
        },
        [updateDistributorCompany, addresses, distributorCompany, emailsKey]
    );

    const handleAddAddress = useCallback(
        (newAddress, callback) => {
            updateDistributorCompany({
                payload: {
                    id: distributorCompany.id,
                    emails: [...addresses, newAddress],
                    emailsKey,
                },
                callback,
            });
        },
        [updateDistributorCompany, addresses, distributorCompany, emailsKey]
    );

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return (
        <ExpandableList title={title}>
            <Box width="400px" marginX="auto">
                {addresses.length < MAX_ADDRESSES && (
                    <CommunicationEmailAdd addresses={addresses} onAdd={handleAddAddress} />
                )}
                {addresses.map((address, i) => (
                    <EditableEmailAddress
                        key={address}
                        onDelete={handleDeleteAddress(i)}
                        onUpdate={handleUpdateAddress(i)}
                        defaultValue={address}
                        deleteDisabled={addresses.length <= MIN_ADDRESSES}
                    />
                ))}
            </Box>
        </ExpandableList>
    );
};

DistributorEmailList.propTypes = {
    emailsKey: PropTypes.string.isRequired,
    title: PropTypes.string,
    distributor: PropTypes.object,
};

export default DistributorEmailList;
